// v10/src/components/admin/lifespan/ByAircraftView.js
//
// Vista "Por aeronave" del módulo Vida límite (A6.1) — Tarea 2.
// Selector de aeronave + grid de cards de componentes con polling, ahora
// con todos los modales reales integrados (Create, Edit, Replacement,
// Attachment, History, Delete).
//
// Tarea 1 dejó preparada la prop `initialAircraftId`; en T2 agregamos
// `initialComponentId` para hacer scroll + highlight de la card cuando
// se entra desde la vista global.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plane, Plus, RefreshCw, AlertCircle, Loader, ShieldCheck, Gauge,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/apiService';
import componentLifespanService from '../../../services/componentLifespanService';
import { useToast } from '../../../hooks/useToast';
import { POLLING_INTERVALS } from '../../../config/realtime';
import AogBadge from '../../aircraft/AogBadge';
import ComponentCard from './ComponentCard';
import CreateComponentModal from './CreateComponentModal';
import EditComponentModal from './EditComponentModal';
import RegisterReplacementModal from './RegisterReplacementModal';
import AttachmentModal from './AttachmentModal';
import ReplacementsHistoryModal from './ReplacementsHistoryModal';
import DeleteComponentModal from './DeleteComponentModal';

const MANAGE_ROLES = new Set(['admin', 'instructor', 'superadmin']);
const DELETE_ROLES = new Set(['admin', 'superadmin']); // instructor NO puede eliminar

const formatHobbs = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
};

const ByAircraftView = ({
  darkMode,
  userRole,
  locale = 'es-AR',
  isActive = true,
  initialAircraftId = null,
  initialComponentId = null,
  onInitialApplied,
}) => {
  const { t } = useTranslation('admin');
  const toast = useToast();

  const [aircraftList,    setAircraftList]    = useState([]);
  const [aircraftLoading, setAircraftLoading] = useState(true);
  const [aircraftError,   setAircraftError]   = useState('');
  const [selectedId,      setSelectedId]      = useState(initialAircraftId || '');

  const [components,        setComponents]        = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [componentsError,   setComponentsError]   = useState('');

  // ── Estado de modales ─────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [replacingComponent, setReplacingComponent] = useState(null);
  const [attachmentComponent, setAttachmentComponent] = useState(null);
  const [historyComponent, setHistoryComponent] = useState(null);
  const [deletingComponent, setDeletingComponent] = useState(null);

  // Highlight cuando se entra desde GlobalAttentionTable
  const [highlightedId, setHighlightedId] = useState(null);
  const cardRefs = useRef({});

  const pollRef = useRef(null);
  const canManage = MANAGE_ROLES.has(userRole);
  const canDelete = DELETE_ROLES.has(userRole);

  // ── Carga de aeronaves activas ─────────────────────────────────
  const loadAircraft = useCallback(async () => {
    setAircraftError('');
    setAircraftLoading(true);
    try {
      const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
      const list = res?.data?.data || [];
      setAircraftList(list);
    } catch (_err) {
      setAircraftError(t('lifespan_by_aircraft_aircraft_load_error'));
      setAircraftList([]);
    } finally {
      setAircraftLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAircraft(); }, [loadAircraft]);

  // Si llega un initialAircraftId después de montar (p. ej. desde vista global)
  useEffect(() => {
    if (initialAircraftId) setSelectedId(initialAircraftId);
  }, [initialAircraftId]);

  // ── Carga de componentes de la aeronave seleccionada ───────────
  const loadComponents = useCallback(async (showLoader = false) => {
    if (!selectedId) {
      setComponents([]);
      setComponentsError('');
      return;
    }
    if (showLoader) setComponentsLoading(true);
    setComponentsError('');
    try {
      const res = await componentLifespanService.getComponents({ aircraft_id: selectedId });
      const data = res?.data?.data || [];
      setComponents(data);
    } catch (_err) {
      setComponentsError(t('lifespan_by_aircraft_load_error'));
    } finally {
      if (showLoader) setComponentsLoading(false);
    }
  }, [selectedId, t]);

  // Carga inicial al cambiar selección
  useEffect(() => {
    if (!selectedId) {
      setComponents([]);
      return;
    }
    loadComponents(true);
  }, [selectedId, loadComponents]);

  // Polling — pausa si la pestaña no está activa o no hay aeronave
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!isActive || !selectedId) return undefined;
    pollRef.current = setInterval(() => loadComponents(false), POLLING_INTERVALS.maintenance);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isActive, selectedId, loadComponents]);

  // ── Scroll + highlight cuando se viene desde Global ───────────────
  useEffect(() => {
    if (!initialComponentId) return;
    const found = components.find((c) => c.id === initialComponentId);
    if (!found) return;
    setHighlightedId(initialComponentId);
    // Scroll suave a la card
    const node = cardRefs.current[initialComponentId];
    if (node?.scrollIntoView) {
      requestAnimationFrame(() => {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    // Notificar al parent para que limpie initialComponentId y no se vuelva a aplicar
    onInitialApplied?.();
    // Limpiar highlight después de 2.5s
    const id = setTimeout(() => setHighlightedId(null), 2500);
    return () => clearTimeout(id);
  }, [initialComponentId, components, onInitialApplied]);

  const selectedAircraft = useMemo(
    () => aircraftList.find((a) => a.id === selectedId) || null,
    [aircraftList, selectedId]
  );

  // ── Handlers de acciones desde la card ────────────────────────────
  const handleCardAction = (action, component) => {
    switch (action) {
      case 'edit':
        setEditingComponent(component);
        break;
      case 'replace':
        setReplacingComponent(component);
        break;
      case 'upload_337':
        setAttachmentComponent(component);
        break;
      case 'view_history':
        setHistoryComponent(component);
        break;
      case 'delete':
        if (canDelete) setDeletingComponent(component);
        break;
      default:
        break;
    }
  };

  const handleSuccess = () => loadComponents(false);

  const handleReplacementSuccess = ({ date, horometer }) => {
    loadComponents(false);
    toast.success(t('component_replacement_modal_success', { date, horometer }));
  };

  // ── Estilos ────────────────────────────────────────────────────
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
    color: fgPrimary,
    fontFamily: 'inherit',
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Selector de aeronave */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <div>
          <label
            htmlFor="lifespan-aircraft-select"
            style={{
              display: 'block', fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              color: fgFaint, marginBottom: 6,
            }}
          >
            {t('lifespan_by_aircraft_select_label')}
          </label>
          <select
            id="lifespan-aircraft-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={aircraftLoading || !!aircraftError}
            style={inputStyle}
          >
            <option value="">{t('lifespan_by_aircraft_select_placeholder')}</option>
            {aircraftList.map((ac) => (
              <option key={ac.id} value={ac.id}>
                {ac.registration} — {ac.manufacturer ? `${ac.manufacturer} ` : ''}{ac.model_name || ''}
              </option>
            ))}
          </select>
          {aircraftError && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#dc2626' }}>
              {aircraftError}
            </p>
          )}
        </div>

        <motion.button
          type="button"
          onClick={() => { loadAircraft(); if (selectedId) loadComponents(true); }}
          whileTap={{ scale: 0.96 }}
          aria-label={t('lifespan_by_aircraft_refresh')}
          title={t('lifespan_by_aircraft_refresh')}
          style={{
            padding: 11, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
            color: fgMuted, display: 'flex',
            alignSelf: 'end',
          }}
        >
          <RefreshCw style={{ width: 16, height: 16 }} />
        </motion.button>
      </div>

      {/* Sin aeronave seleccionada */}
      {!selectedId && !aircraftLoading && !aircraftError && (
        <EmptyState
          icon={Plane}
          title={t('lifespan_by_aircraft_no_aircraft_selected')}
          desc={t('lifespan_by_aircraft_no_aircraft_selected_desc')}
          darkMode={darkMode}
        />
      )}

      {/* Header de la aeronave seleccionada */}
      {selectedAircraft && (
        <div
          style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
            justifyContent: 'space-between',
            padding: '14px 16px', borderRadius: 16,
            background: darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)',
            border: darkMode ? '1px solid rgba(99,102,241,0.20)' : '1px solid rgba(99,102,241,0.18)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(79,70,229,0.28)',
                flexShrink: 0,
              }}
            >
              <Plane style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <p
                  style={{
                    margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary,
                    letterSpacing: '-0.2px',
                  }}
                >
                  {selectedAircraft.registration}
                </p>
                <AogBadge aircraft={selectedAircraft} size="sm" />
              </div>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
                {selectedAircraft.manufacturer ? `${selectedAircraft.manufacturer} ` : ''}
                {selectedAircraft.model_name || ''}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 999,
                background: darkMode ? 'rgba(255,255,255,0.06)' : 'white',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                fontSize: 12, fontWeight: 700, color: fgPrimary,
              }}
              title={t('lifespan_by_aircraft_horometer')}
            >
              <Gauge style={{ width: 13, height: 13, color: '#6366f1' }} />
              {t('lifespan_by_aircraft_horometer')}:&nbsp;
              <strong>{formatHobbs(selectedAircraft.hobbs_current, locale)} h</strong>
            </div>

            {canManage && (
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setShowCreate(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                  color: 'white', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                  boxShadow: '0 6px 16px rgba(79,70,229,0.28)',
                }}
              >
                <Plus style={{ width: 14, height: 14 }} />
                {t('lifespan_by_aircraft_add_component')}
              </motion.button>
            )}
          </div>
        </div>
      )}

      {/* Error de componentes con botón Reintentar */}
      {selectedId && componentsError && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '12px 16px', borderRadius: 14,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.25)',
            color: '#dc2626', fontSize: 13, fontWeight: 600,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
            {componentsError}
          </span>
          <button
            type="button"
            onClick={() => loadComponents(true)}
            style={{
              padding: '6px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: '#dc2626', color: 'white',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
            }}
          >
            {t('lifespan_by_aircraft_retry')}
          </button>
        </div>
      )}

      {/* Loading inicial de componentes */}
      {selectedId && componentsLoading && components.length === 0 && !componentsError && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            minHeight: 180, color: fgMuted, fontSize: 14,
          }}
        >
          <style>{`@keyframes lifespanSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
          <Loader style={{ width: 18, height: 18, animation: 'lifespanSpin 1s linear infinite' }} />
          {t('lifespan_by_aircraft_loading')}
        </div>
      )}

      {/* Empty state — aeronave seleccionada sin componentes */}
      {selectedId && !componentsLoading && !componentsError && components.length === 0 && (
        <EmptyState
          icon={ShieldCheck}
          title={t('lifespan_by_aircraft_empty_title')}
          desc={t('lifespan_by_aircraft_empty_desc')}
          darkMode={darkMode}
          cta={canManage ? {
            label: t('lifespan_by_aircraft_add_component'),
            onClick: () => setShowCreate(true),
          } : null}
        />
      )}

      {/* Grid de cards */}
      {selectedId && components.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 16,
          }}
        >
          {components.map((c) => {
            const isHighlighted = c.id === highlightedId;
            return (
              <div
                key={c.id}
                ref={(el) => {
                  if (el) cardRefs.current[c.id] = el;
                }}
                style={{
                  borderRadius: 22,
                  outline: isHighlighted ? '2px solid #6366f1' : 'none',
                  outlineOffset: 2,
                  boxShadow: isHighlighted ? '0 12px 36px rgba(99,102,241,0.32)' : 'none',
                  transition: 'outline 0.3s, box-shadow 0.3s',
                }}
              >
                <ComponentCard
                  component={c}
                  darkMode={darkMode}
                  canManage={canManage}
                  canDelete={canDelete}
                  locale={locale}
                  onAction={handleCardAction}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Modales */}
      <AnimatePresence>
        {showCreate && selectedAircraft && (
          <CreateComponentModal
            aircraft={selectedAircraft}
            darkMode={darkMode}
            onSuccess={handleSuccess}
            onClose={() => setShowCreate(false)}
          />
        )}
        {editingComponent && (
          <EditComponentModal
            component={editingComponent}
            darkMode={darkMode}
            onSuccess={handleSuccess}
            onClose={() => setEditingComponent(null)}
          />
        )}
        {replacingComponent && (
          <RegisterReplacementModal
            component={replacingComponent}
            darkMode={darkMode}
            locale={locale}
            onSuccess={handleReplacementSuccess}
            onClose={() => setReplacingComponent(null)}
          />
        )}
        {attachmentComponent && (
          <AttachmentModal
            component={attachmentComponent}
            darkMode={darkMode}
            onSuccess={handleSuccess}
            onClose={() => setAttachmentComponent(null)}
          />
        )}
        {historyComponent && (
          <ReplacementsHistoryModal
            component={historyComponent}
            darkMode={darkMode}
            locale={locale}
            onClose={() => setHistoryComponent(null)}
          />
        )}
        {deletingComponent && (
          <DeleteComponentModal
            component={deletingComponent}
            aircraft={selectedAircraft}
            darkMode={darkMode}
            onSuccess={handleSuccess}
            onClose={() => setDeletingComponent(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, desc, darkMode, cta = null }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 14, padding: '48px 24px',
        borderRadius: 20,
        border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
        background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56, height: 56, borderRadius: 18,
          background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon style={{ width: 26, height: 26, color: '#6366f1' }} />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary }}>{title}</p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: fgMuted, maxWidth: 460 }}>{desc}</p>
      </div>
      {cta && (
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={cta.onClick}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 16px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
            color: 'white', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
            boxShadow: '0 6px 16px rgba(79,70,229,0.28)',
          }}
        >
          <Plus style={{ width: 15, height: 15 }} />
          {cta.label}
        </motion.button>
      )}
    </div>
  );
};

export default ByAircraftView;
